import { Center, Loader } from '@hover/blueprint';
import { ensureError } from '@hover/utilities';
import React, { useEffect, useState } from 'react';
import {
  Redirect,
  generatePath,
  useHistory,
  useRouteMatch,
} from 'react-router';
import { number, object, string } from 'yup';

import { ImperativeTrackingProvider } from 'src/modules/homeownerApp/components/Customize/useImperativeTracking';
import { EmbedFooter } from 'src/modules/homeownerApp/components/Embed/components/EmbedFooter';
import { useAvailableMaterialsRegions } from 'src/modules/homeownerApp/components/Palette';
import { getImageAspect } from 'src/modules/homeownerApp/helpers';
import { useStickyDepth } from 'src/modules/homeownerApp/hooks';
import {
  useAiDisplayImageQuery,
  useAiGeneratedImageRegionsQuery,
  useAiGeneratedImageSegmentationQuery,
  useDetectAiRegionsMutation,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useIsEmbed,
  useParsedParams,
  useTimeout,
  useTranslate,
} from 'src/shared/hooks';
import { Route } from 'src/shared/route';
import { logError } from 'src/shared/telemetry/logger';

import { PartnerImageProvider } from '../../hooks/usePartner';
import { UploadNotFoundSplash } from '../OriginalImageScreen/UploadNotFoundSplash';

import { Customize } from './Customize';
import { SubRegionsProvider } from './SubRegions';
import {
  getCustomizeMenuForImage,
  getEnabledRegions,
  paintRegionsForImage,
  routeParamsToCategory,
} from './menu';
import { CustomizationProvider } from './useCustomization';
import { useCustomizeReferrerState } from './useCustomizeReferrerState';
import { useIsSubregionSelectionEnabled } from './useIsSubregionSelectionEnabled';
import { PaletteProvider } from './usePalettes';

const IMAGE_SEGMENTATION_POLLING_INTERVAL = 1000;

export const useDetectedRegions = ({
  aiGeneratedImageId,
  spaceType,
}: {
  aiGeneratedImageId: number | undefined;
  spaceType?: string | null;
}) => {
  const isSubregionSelectionEnabled = useIsSubregionSelectionEnabled();

  // Add a delay before allowing the Loading Overlay to be displayed. This ensures short loading times don't
  // flash the Loading Overlay which is visually distracting. For longer loading times, the overlay will fade in.
  const [allowLoadingOverlay, setAllowLoadingOverlay] = useState(false);
  const [allowLoadingOverlayTimeout] = useTimeout();

  useEffect(() => {
    allowLoadingOverlayTimeout(() => {
      setAllowLoadingOverlay(true);
    }, 1000);
  }, [allowLoadingOverlayTimeout]);

  const isExterior = getImageAspect({ spaceType }) === 'exterior';
  const skipQuery = typeof aiGeneratedImageId === 'undefined' || !isExterior;

  const imageId = aiGeneratedImageId ? String(aiGeneratedImageId) : '0';

  const { data: regionsData, error: regionError } =
    useAiGeneratedImageRegionsQuery({
      skip: skipQuery,
      variables: {
        id: imageId,
      },
    });

  const {
    data: segmentationData,
    startPolling,
    stopPolling,
  } = useAiGeneratedImageSegmentationQuery({
    skip: skipQuery || !isSubregionSelectionEnabled,
    variables: {
      id: imageId,
    },
    fetchPolicy: 'no-cache',
    onError: () => {
      stopPolling();
    },
  });

  const [detectAiRegionsMutation, { loading: detectAiLoading }] =
    useDetectAiRegionsMutation();

  useEffect(() => {
    const updateDetectAiRegions = async () => {
      try {
        const { data } = await detectAiRegionsMutation({
          variables: {
            aiGeneratedImageId: Number(imageId),
          },
        });
        if (data?.detectAiRegions.id) {
          startPolling(IMAGE_SEGMENTATION_POLLING_INTERVAL);
        }
      } catch (err) {
        logError(ensureError(err));
      }
    };

    if (
      segmentationData &&
      !segmentationData?.aiGeneratedImage?.aiSegmentation &&
      isExterior &&
      isSubregionSelectionEnabled
    ) {
      void updateDetectAiRegions();
    }
    if (
      segmentationData &&
      segmentationData?.aiGeneratedImage?.aiSegmentation
    ) {
      stopPolling();
    }
  }, [
    segmentationData,
    detectAiRegionsMutation,
    imageId,
    startPolling,
    stopPolling,
    isExterior,
    isSubregionSelectionEnabled,
  ]);

  const isGeneratingSegmentation =
    !segmentationData?.aiGeneratedImage?.aiSegmentation &&
    isSubregionSelectionEnabled;

  const loading = isExterior
    ? (!regionsData && !regionError) ||
      detectAiLoading ||
      isGeneratingSegmentation
    : false;

  return {
    allowLoadingOverlay,
    regionsData,
    segmentationData,
    // Instead of using the loading return value of the query, we use the lack
    // of data and errors to indicate loading. This ensures we return
    // loading: true if the query is skipped
    loading,
  };
};

const CustomizeRoute = () => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);

  const {
    aiDisplayImageId,
    category: categorySlug,
    region: regionSlug,
  } = useParsedParams(
    object({
      aiDisplayImageId: number().required(),
      category: string(),
      region: string(),
    }),
  );

  const history = useHistory();
  const state = useCustomizeReferrerState();

  const { data: displayImageData, loading: isLoading } = useAiDisplayImageQuery(
    {
      variables: { id: aiDisplayImageId ?? 0 },
      skip: !aiDisplayImageId,
    },
  );

  const availableMaterialsRegions = useAvailableMaterialsRegions();

  const displayImage = displayImageData?.aiDisplayImage;

  const {
    regionsData,
    segmentationData,
    loading: regionsLoading,
    allowLoadingOverlay,
  } = useDetectedRegions({
    aiGeneratedImageId: displayImage?.aiGeneratedImage.id,
    spaceType: displayImage?.spaceType,
  });

  const isEmbed = useIsEmbed();
  // Inspire view only visible while in embed experience
  const isInspire =
    !!useRouteMatch(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path) && isEmbed;

  const embedFooterHeight = { base: '100px', tablet: '90px' };
  const stickyDepth = useStickyDepth();

  if (!aiDisplayImageId) return <Redirect to={Route.HOMEOWNER_APP_HOME.path} />;

  if (isLoading) {
    return (
      <Center
        bg="neutral.900"
        bottom="0"
        left="0"
        position="fixed"
        right="0"
        top="0"
      >
        <Loader color="neutral.0" size="huge" />
      </Center>
    );
  }

  const menu = getCustomizeMenuForImage(
    t,
    displayImage,
    regionsData?.aiGeneratedImage,
    availableMaterialsRegions,
  );

  if (!displayImage || !menu) return <UploadNotFoundSplash />;

  const category = routeParamsToCategory(
    displayImage,
    categorySlug,
    regionSlug,
  );
  const fromSample = displayImage.aiGeneratedImage?.fromSample;

  // Inspire will not have categories but will have a category slug for routing
  if (categorySlug && !category && !isInspire) {
    return (
      <Redirect
        to={generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
          aiDisplayImageId: displayImage.id,
        })}
      />
    );
  }

  return (
    <CustomizationProvider
      category={category}
      segmentationData={segmentationData}
      targetImage={displayImage}
    >
      <PartnerImageProvider aiDisplayImageId={displayImage.id}>
        <ImperativeTrackingProvider includeCustomizeTargetTracking>
          <PaletteProvider>
            <SubRegionsProvider
              getSubRegionLabel={region => ({
                toggle: t(`customize.regionsAll.${region}`),
                region: t(`customize.regions.${region}`),
              })}
              onSelectSubRegion={region =>
                history.replace({
                  pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
                    aiDisplayImageId: displayImage.id,
                    category: 'paint',
                    region,
                  }),
                  state,
                })
              }
              selectedSubRegion={
                category && category.type === 'paint'
                  ? category.region
                  : 'walls'
              }
              subRegions={
                category?.type === 'paint'
                  ? getEnabledRegions(
                      paintRegionsForImage(displayImage),
                      regionsData?.aiGeneratedImage,
                    )
                  : []
              }
            >
              <Customize
                allowLoadingOverlay={allowLoadingOverlay && !isInspire}
                bottom={fromSample ? embedFooterHeight : 0}
                isInspire={isInspire}
                isRegionsLoading={regionsLoading}
                menu={menu}
              />
              {fromSample && (
                <EmbedFooter
                  bottom={0}
                  height={embedFooterHeight}
                  left={0}
                  position="fixed"
                  right={0}
                  zIndex={stickyDepth + 2}
                />
              )}
            </SubRegionsProvider>
          </PaletteProvider>
        </ImperativeTrackingProvider>
      </PartnerImageProvider>
    </CustomizationProvider>
  );
};

export { CustomizeRoute };
